import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { User, Mail, Link as LinkIcon, FileText, Save, Loader2, Camera, CheckCircle } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import DesignerLayout from './DesignerLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DesignerProfile = () => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    bio: '',
    portfolio_url: '',
    avatar_url: '',
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('user_token');
      if (!token || !API_URL) {
        // Load from localStorage as fallback
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          setForm(f => ({ ...f, name: u.name || '', email: u.email || '' }));
        } catch {}
        return;
      }
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setForm(f => ({
            ...f,
            name: data.name || '',
            email: data.email || '',
            bio: data.bio || '',
            portfolio_url: data.portfolio_url || '',
            avatar_url: data.avatar_url || '',
          }));
          // Update localStorage too
          localStorage.setItem('user', JSON.stringify(data));
        }
      } catch {
        try {
          const u = JSON.parse(localStorage.getItem('user') || '{}');
          setForm(f => ({ ...f, name: u.name || '', email: u.email || '' }));
        } catch {}
      }
    };
    fetchProfile();
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast({ title: 'Name required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    setSaved(false);

    // Update localStorage immediately for display
    try {
      const current = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...current, name: form.name }));
    } catch {}

    // Attempt API update if available
    const token = localStorage.getItem('user_token');
    if (API_URL && token) {
      try {
        await fetch(`${API_URL}/api/auth/me`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: form.name, bio: form.bio, portfolio_url: form.portfolio_url }),
        });
      } catch {
        // Silently ignore — profile saved locally
      }
    }

    setSaving(false);
    setSaved(true);
    toast({ title: 'Profile saved', description: 'Your changes have been applied.' });
    setTimeout(() => setSaved(false), 3000);
  };

  const initials = form.name
    ? form.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : 'D';

  return (
    <DesignerLayout>
      <div className="space-y-6 w-full">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
          <p className="text-gray-500 text-sm mt-0.5">Manage your designer profile and preferences.</p>
        </div>

        <form onSubmit={handleSave} className="space-y-5">

          {/* Avatar + name */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-sm font-semibold text-gray-700 mb-5">Designer Identity</h2>
            <div className="flex items-start gap-6">
              {/* Avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white text-2xl font-bold">
                  {form.avatar_url ? (
                    <img src={form.avatar_url} alt="Avatar" className="w-full h-full object-cover rounded-2xl" />
                  ) : initials}
                </div>
                <button
                  type="button"
                  className="absolute -bottom-2 -right-2 w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
                  title="Change photo (paste URL below)"
                >
                  <Camera className="w-3.5 h-3.5 text-gray-500" />
                </button>
              </div>

              <div className="flex-1 space-y-4">
                <div className="space-y-2">
                  <Label>Display Name *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={form.name}
                      onChange={e => set('name', e.target.value)}
                      placeholder="Your full name"
                      className="pl-9"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Avatar URL</Label>
                  <div className="relative">
                    <Camera className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={form.avatar_url}
                      onChange={e => set('avatar_url', e.target.value)}
                      placeholder="https://example.com/avatar.jpg"
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* About */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700">About You</h2>

            <div className="space-y-2">
              <Label>Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={form.email}
                  className="pl-9 bg-gray-50 text-gray-500 cursor-not-allowed"
                  disabled
                  readOnly
                />
              </div>
              <p className="text-xs text-gray-400">Email cannot be changed.</p>
            </div>

            <div className="space-y-2">
              <Label>Bio</Label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <textarea
                  value={form.bio}
                  onChange={e => set('bio', e.target.value)}
                  placeholder="Tell the world a bit about yourself and your design style…"
                  rows={4}
                  className="w-full pl-9 pr-3 py-2.5 rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
              </div>
              <p className="text-xs text-gray-400">{(form.bio || '').length}/300 characters</p>
            </div>

            <div className="space-y-2">
              <Label>Portfolio Link</Label>
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="url"
                  value={form.portfolio_url}
                  onChange={e => set('portfolio_url', e.target.value)}
                  placeholder="https://yourportfolio.com"
                  className="pl-9"
                />
              </div>
            </div>
          </div>

          {/* Account Info */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
            <h2 className="text-sm font-semibold text-gray-700">Account</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-400 font-medium">Account Type</p>
                <p className="font-semibold text-gray-800 mt-0.5">Designer</p>
              </div>
              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-400 font-medium">Portal Access</p>
                <p className="font-semibold text-gray-800 mt-0.5">iVisora Creator</p>
              </div>
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-xs text-amber-700 font-medium">Password Change</p>
              <p className="text-xs text-amber-600 mt-0.5">
                To change your password, contact the platform administrator.
              </p>
            </div>
          </div>

          {/* Save */}
          <div className="flex items-center justify-end gap-3">
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
                <CheckCircle className="w-4 h-4" /> Saved
              </span>
            )}
            <Button
              type="submit"
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700 gap-1.5 px-6"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Profile</>}
            </Button>
          </div>
        </form>
      </div>
    </DesignerLayout>
  );
};

export default DesignerProfile;
